import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  Header,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
  Flashbar,
} from "@amzn/awsui-components-react";
import { useNavigate } from "react-router-dom";
import { COLUMN_DEFINITIONS } from "../config/searchTableConfig";
import { useSearchHistory } from "../hooks/useSearchHistory";
import useFolders from "../hooks/useFolders";
import { SavedSearchItem, SearchHistoryItem } from "../types/common";
import { FolderManagement } from "./folders/FolderManagement";

export default function SearchHistory() {
  const navigate = useNavigate();
  const textFilterRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [folderName, setFolderName] = useState("");
  const [error, setError] = useState("");

  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    deleteSelectedItems,
  } = useSearchHistory();
  const { folders, addTransactions, saveFolder } = useFolders();

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleModalDismiss = () => {
    setIsModalVisible(false);
    setSelectedFolderId("");
  };

  const handleCreateFolder = () => {
    setIsModalVisible(false);
    setIsFolderModalVisible(true);
    setFolderName("");
    setError("");
  };

  const handleFolderModalDismiss = () => {
    setIsFolderModalVisible(false);
    setFolderName("");
    setError("");
  };

  const handleSaveFolder = () => {
    if (!folderName.trim()) {
      setError("Please enter a folder name");
      return;
    }
    saveFolder(folderName);
    handleFolderModalDismiss();
    setIsModalVisible(true);
    setNotification({
      type: "success",
      message: "Folder created successfully",
    });
  };

  const handleAddToFolder = () => {
    if (!collectionProps.selectedItems?.length) {
      return;
    }
    setIsModalVisible(true);
  };

  const handleFolderSelect = () => {
    if (selectedFolderId && collectionProps.selectedItems?.length) {
      const selectedTransactions = (
        collectionProps.selectedItems as SearchHistoryItem[]
      ).map(
        (item) =>
          ({
            id: item.orderId,
            stage: item.stage,
            status: item.status,
            country: item.countryCode,
            gl: item.glProductGroup,
            usecase: "",
            paymentMethod: "",
          } as SavedSearchItem)
      );

      const result = addTransactions(selectedFolderId, selectedTransactions);

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });

      if (result.addedCount > 0 && collectionProps.onSelectionChange) {
        collectionProps.onSelectionChange({ detail: { selectedItems: [] } });
      }
      handleModalDismiss();
    }
  };

  const selectedOption = folders.find((f) => f.id === selectedFolderId)
    ? {
        label: folders.find((f) => f.id === selectedFolderId)?.name || "",
        value: selectedFolderId,
      }
    : null;

  const flashbarItems = notification
    ? [
        {
          type: notification.type,
          content: (
            <SpaceBetween direction="vertical" size="xs">
              {notification.message}
              {notification.type === "success" && (
                <Button variant="normal" onClick={() => navigate("/saved")}>
                  Go to Folders
                </Button>
              )}
            </SpaceBetween>
          ),
          dismissible: true,
          onDismiss: () => setNotification(null),
        },
      ]
    : [];

  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: "2vh",
          right: "2vw",
          width: "30vw",
          minWidth: "300px",
          maxWidth: "600px",
          zIndex: 1000,
          fontSize: "calc(14px + 0.3vw)",
        }}
      >
        {flashbarItems.length > 0 && <Flashbar items={flashbarItems} />}
      </div>
      <Table
        {...collectionProps}
        resizableColumns
        stickyHeader
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        loadingText="Loading resources"
        selectionType="multi"
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {process.env.NODE_ENV === "development" && (
                  <Button
                    onClick={handleAddToFolder}
                    disabled={collectionProps.selectedItems?.length === 0}
                  >
                    Add to Folder
                  </Button>
                )}
                <Button
                  onClick={deleteSelectedItems}
                  disabled={collectionProps.selectedItems?.length === 0}
                >
                  Delete selected
                </Button>
              </SpaceBetween>
            }
          >
            Recent Searches
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            ref={textFilterRef}
            filteringPlaceholder="Filter by Order ID, GL, Country, Stage, or Status"
          />
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No recent searches</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              Transactions you search for will appear here.
            </Box>
          </Box>
        }
      />
      <Modal
        visible={isModalVisible}
        onDismiss={handleModalDismiss}
        header="Select Folder"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleModalDismiss}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleFolderSelect}
                disabled={!selectedFolderId}
              >
                Add to Folder
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          {folders.length === 0 ? (
            <Box textAlign="center">
              <SpaceBetween direction="vertical" size="s">
                <Box variant="p">No folders found</Box>
                <Button onClick={handleCreateFolder} iconName="add-plus">
                  Create New Folder
                </Button>
              </SpaceBetween>
            </Box>
          ) : (
            <>
              <Select
                selectedOption={selectedOption}
                onChange={({ detail }) =>
                  setSelectedFolderId(detail.selectedOption.value || "")
                }
                options={folders.map((folder) => ({
                  label: folder.name,
                  value: folder.id,
                }))}
                placeholder="Choose a folder"
              />
              <Button onClick={handleCreateFolder} iconName="add-plus">
                Create New Folder
              </Button>
            </>
          )}
        </SpaceBetween>
      </Modal>
      <FolderManagement
        visible={isFolderModalVisible}
        onDismiss={handleFolderModalDismiss}
        onSave={handleSaveFolder}
        folderName={folderName}
        setFolderName={setFolderName}
        isEditing={false}
        error={error}
      />
    </>
  );
}
